import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  analysisApi,
  FlatUnsettleBetsEvent,
  GroupedUnsettleBetsGame,
} from "src/api/analysisApi";
import { useSyncGlobalPageLoader } from "src/contexts/global-page-loader-context";
import { formatUnix } from "src/utils/date";
import { parseNumber } from "src/utils/functions";

const MarketAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<GroupedUnsettleBetsGame[]>([]);

  useSyncGlobalPageLoader(loading);

  const fetchData = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await analysisApi.fetchGroupedUnsettleBets();
      if (!response.response_error && response.data) {
        setList(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Failed to fetch sport analysis:", error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const flatEvents = useMemo(() => {
    const nEvents: FlatUnsettleBetsEvent[] = [];

    list.forEach((game) => {
      game.leagues.forEach((league) => {
        league.events.forEach((event) => {
          nEvents.push({
            ...event,
            game_id: game.game_id,
            game_name: game.game_name,
            league_name: league.league_name,
            event_time: event.event_time ?? "",
          });
        });
      });
    });

    return nEvents;
  }, [list]);

  return (
    <div className="listing-grid">
        <div className="market-analysis">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0 font-size-18">
                  Market Analysis
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      fetchData();
                    }}
                    title="Refresh Data"
                    className="text-dark pl-2"
                  >
                    <i
                      className={clsx("fa fa-sync", {
                        "fa-spin": loading,
                      })}
                    />
                  </a>
                </h4>
                <div className="page-title-right">
                  <input
                    type="text"
                    name="searchMarktetText"
                    value=""
                    placeholder="Search Event"
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="market-analysis-container">
            {Array.isArray(flatEvents) &&
              flatEvents.map((event, index) => {
                return (
                  <div className="market-analysis-container" key={index + event.event_id}>
                    <div className="market-analysis-title">
                      <div>
                        <Link to={`/game/details/${event.game_id}/${event.event_id}`} className="ma-link">
                          {event.event_name}
                        </Link>
                      </div>
                      <div>{formatUnix({ unix: event.event_time, format: "DD/MM/YYYY HH:mm:ss" })}</div>
                    </div>
                    <div className="market-analysis-content">
                      <div className="row row5">
                        <div className="col-lg-4">
                          <div data-simplebar="init" className="market-analysis-content-detail">
                            <div className="simplebar-wrapper" style={{ margin: 0 }}>
                              <div className="simplebar-height-auto-observer-wrapper">
                                <div className="simplebar-height-auto-observer"></div>
                              </div>
                              <div className="simplebar-mask">
                                <div className="simplebar-offset" style={{ right: 0, bottom: 0 }}>
                                  <div
                                    className="simplebar-content-wrapper"
                                    tabIndex={0}
                                    role="region"
                                    aria-label="scrollable content"
                                    style={{ height: "auto", overflow: "hidden" }}
                                  >
                                    <div className="simplebar-content" style={{ padding: 0 }}>
                                      <table className="table">
                                        <thead>
                                          <tr>
                                            <th colSpan={2}>Normal</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {Array.isArray(event.markets) &&
                                            event.markets.map((market, mIndex) => {
                                              return (
                                                <tr key={mIndex + market.market_name}>
                                                  <td>{market.market_name}</td>
                                                  <td className="text-right">
                                                    {parseNumber({ type: "float", value: market.exposure, toFixed: 2 })}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="simplebar-placeholder" style={{ width: "auto", height: 94 }}></div>
                            </div>
                            <div className="simplebar-track simplebar-horizontal" style={{ visibility: "hidden" }}>
                              <div className="simplebar-scrollbar" style={{ width: 0, display: "none" }}></div>
                            </div>
                            <div className="simplebar-track simplebar-vertical" style={{ visibility: "hidden" }}>
                              <div className="simplebar-scrollbar" style={{ height: 0, display: "none" }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
  );
};

export default MarketAnalysis;
